import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Button,
  Modal,
  Typography,
  Paper
} from '@mui/material';

interface Category {
  name: string;
  priority: number;
  featured: boolean;
}

interface CategoryChipsProps {
  categories: Category[];
  selectedCategories: string[];
  onChange: (categories: Category[]) => void;
  maxInitialDisplay?: number;
  noTitle?: boolean;
  openModal?: boolean;
  onModalClose?: () => void;
  isSeeMoreVisibel?: boolean;
  onChipClick?: (categoryName: string, isSelected: boolean) => void;
}

const CategoryChips: React.FC<CategoryChipsProps> = ({
  categories,
  selectedCategories,
  onChange,
  maxInitialDisplay = 5,
  noTitle,
  openModal,
  onModalClose,
  isSeeMoreVisibel = true,
  onChipClick,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  
  useEffect(() => {
    if (openModal !== undefined) {
      setModalOpen(openModal);
    }
  }, [openModal]);
  
  const handleModalClose = () => {
    setModalOpen(false);
    onModalClose?.();
  };


  // Helper to get all categories sorted for the modal
  const getDisplayCategoriesModal = () => {
    const selected = categories.filter(cat => selectedCategories.includes(cat.name));
    const featured = categories.filter(cat => cat.featured && !selectedCategories.includes(cat.name));
    const prioritized = categories
      .filter(cat => !selectedCategories.includes(cat.name) && !cat.featured && typeof cat.priority === 'number')
      .sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity));
    const others = categories.filter(
      cat => !selectedCategories.includes(cat.name) && !cat.featured && typeof cat.priority !== 'number'
    );
    return [...selected, ...featured, ...prioritized, ...others];
  };

  const handleCategoryToggle = (category: Category) => {
    console.log(category.name);
    const newSelected = selectedCategories.includes(category.name)
      ? selectedCategories.filter(cat => cat !== category.name)
      : [...selectedCategories, category.name];
    console.log(newSelected);
    onChange(newSelected.map(name => categories.find(cat => cat.name === name)!));
    onChipClick?.(category.name, newSelected.includes(category.name));
  };

  const displayedCategories = getDisplayCategoriesModal().slice(0, maxInitialDisplay);
  const hasMoreCategories = categories.length && isSeeMoreVisibel && categories.length > maxInitialDisplay ;
  const remainingCategories = categories.slice(maxInitialDisplay);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        {!noTitle && (
          <Typography variant="subtitle1" fontWeight="500" gutterBottom color="black">
            Categories
          </Typography>
        )}
        {hasMoreCategories && (
          <Typography variant='caption' gutterBottom color="#008387" onClick={() => setModalOpen(true)} sx={{ cursor: 'pointer' }}>
            See All
          </Typography>
        )}
      </Box>
      <Box sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 1,
        mb: categories.length > maxInitialDisplay ? 1 : 0,
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari and Opera
        },
        '& .MuiChip-root': {
          flex: '0 0 auto',
          minWidth: 'fit-content'
        }
      }}>
        {displayedCategories.map((categoryItem) => (
          <Chip
            variant={selectedCategories.includes(categoryItem.name) ? 'filled' : 'outlined'}
            key={categoryItem.name}
            label={categoryItem.name}
            onClick={() => handleCategoryToggle(categoryItem)}
            sx={{
              color: selectedCategories.includes(categoryItem.name) ? 'black' : 'black',
              backgroundColor: selectedCategories.includes(categoryItem.name)
                ? '#FEB380'
                : '#ffffff',
              borderRadius: "8px",
              borderColor:'#D0C4BF',
              borderWidth:'1px'
            }}
          />
        ))}
      </Box>

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
      // aria-labelledby="categories-modal"
      >
        <Paper sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50%',
          maxWidth: '250px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '80vh',
          overflow: 'auto',
          borderRadius: '24px',
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Categories
          </Typography>
          <Box sx={{
            mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%',
            '& .MuiChip-root': {
              flex: '1 1 auto',
              minWidth: 'fit-content'
            }
          }}>
            {getDisplayCategoriesModal().map((category) => (
              <Chip
                variant={selectedCategories.includes(category.name) ? 'filled' : 'outlined'}
                key={category.name}
                label={category.name}
                onClick={() => handleCategoryToggle(category)}
                sx={{
                  color: 'black',
                  backgroundColor: selectedCategories.includes(category.name) ? '#FEB380' : '#ffffff',
                  borderRadius: 2,
                }}
              />
            ))}
          </Box>
          {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Typography variant='caption' gutterBottom sx={{ justifyContent: 'center', alignItems: 'center' }}>
              {selectedCategories.length} selected
            </Typography>
            <Button variant='contained' sx={{ borderRadius: "24px", px: 2, ml: 2, bgcolor: '#F57C00' }} color='primary' onClick={handleModalClose}>
              Save
            </Button>
          </Box> */}
        </Paper>
      </Modal>
    </Box>
  );
};

export default CategoryChips; 